import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from "@material-ui/icons";
import { SignatureTemplate } from "../../../@types/signatures";
import { SendRenderContext } from "../../../services/signaturesHtmlSend";
import IframeTemplatePreview from "./IframeTemplatePreview";
import { SignatureDocumentPreview } from "./SignatureDocumentPreview";

// User-driven zoom multiplier compounds on top of PDF fit-to-
// width — 1× = "fit". Bounds chosen so we don't shrink past unreadable nor
// blow up beyond three times the available document width.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/** A shared PDF preview for resolved template content and archived documents. */
export type PreviewPlaceholderField = {
  id: string;
  label: string;
};

export type DocumentPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Live template preview source. When provided alongside `ctx`, the dialog
   * renders a prepared PDF with its measured field overlays. Falls back to `html`/`url` if `template` or
   * `ctx` is missing.
   */
  template?: SignatureTemplate | null;
  ctx?: SendRenderContext | null;
  /** Pre-rendered HTML to compile into the prepared PDF. Used when no template+ctx. */
  html?: string | null;
  /** External file URL (signed PDF). Used when no template+ctx and no html. */
  url?: string | null;
  /** Optional title shown in the header — usually the document number + name. */
  title?: string;
  /**
   * Bundle preview mode: one entry per template in the bundle. When provided
   * with length >= 1, the header renders a Select to switch between docs and
   * the body shows the active doc's pre-rendered HTML. Takes precedence over
   * the single `html` prop — callers in bundle mode still pass `html` (the
   * synthetic merged template's HTML) so the legacy single-doc path stays
   * intact when this is empty/null.
   */
  bundleDocs?: Array<{ id: string; label: string; html: string }> | null;
  /**
   * Legacy placeholder list, kept for back-compat with existing callers but
   * unused — the PDF manifest supplies field labels and positions.
   */
  placeholderFields?: PreviewPlaceholderField[];
  /** Editable sender-entered values shown on top of the unchanged PDF. */
  prefilledValues?: Record<string, string>;
  /** Stacking override for callers rendering this from inside the signatures
   *  detail side-panel — a hand-rolled fixed overlay at zIndex 2001 that a
   *  default 1300 modal opens underneath. */
  zIndex?: number;
};

export default function DocumentPreviewDialog({
  open,
  onClose,
  template,
  ctx,
  html,
  url,
  title,
  bundleDocs,
  prefilledValues,
  zIndex
}: DocumentPreviewDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const hasBundleDocs = !!bundleDocs && bundleDocs.length > 0;
  // Active doc id is tracked locally. Reset to the first doc whenever the
  // dialog opens or the docs list identity changes so switching bundles
  // doesn't strand us on a no-longer-present id.
  const [activeBundleDocId, setActiveBundleDocId] = useState<string | null>(null);
  useEffect(() => {
    if (open && hasBundleDocs) {
      setActiveBundleDocId(bundleDocs![0].id);
    }
  }, [open, hasBundleDocs, bundleDocs]);
  const activeBundleDoc = useMemo(() => {
    if (!hasBundleDocs) return null;
    return bundleDocs!.find((d) => d.id === activeBundleDocId) || bundleDocs![0];
  }, [hasBundleDocs, bundleDocs, activeBundleDocId]);

  // Mode resolution: template+ctx wins, then bundle, then html, then url.
  // Drives both which DOM we render in the body and which header controls show.
  const showInline = !!template && !!ctx;
  const effectiveHtml = hasBundleDocs ? activeBundleDoc?.html ?? "" : html;
  const showHtml = !showInline && !!effectiveHtml;
  const showUrl = !showInline && !showHtml && !!url;

  // User multiplier on top of the PDF's auto fit-to-width. Reset whenever
  // the dialog re-opens or the rendered html changes (including a bundle
  // doc switch) so a previous zoom doesn't carry into a new document.
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (open) setZoom(1);
  }, [open, effectiveHtml]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      // `100dvh` on mobile, not `100vh`. Mobile browsers report `vh`
      // as the LARGEST viewport (chrome fully retracted), so a `100vh`
      // Paper overflows the visible viewport when the address bar is
      // showing and its top — title bar + close button — sits under
      // the chrome. `100dvh` tracks the current visible viewport so
      // the close button is always reachable.
      PaperProps={{ sx: { height: isMobile ? "100dvh" : "85vh" } }}
      sx={zIndex ? { zIndex } : undefined}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1,
          // Solid bg + border so the bar stays clearly distinct from the
          // white document content underneath — without this the title and
          // close button visually merged into the document on mobile and
          // realtors couldn't find a way out of the preview.
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        {hasBundleDocs ? (
          <Select
            size="small"
            value={activeBundleDoc?.id || ""}
            onChange={(e) => setActiveBundleDocId(String(e.target.value))}
            sx={{ flex: 1, minWidth: 0, maxWidth: 360 }}
          >
            {bundleDocs!.map((d, idx) => (
              <MenuItem key={d.id} value={d.id}>
                {idx + 1}. {d.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="subtitle1" sx={{ flex: 1 }} noWrap>
            {title || t("signatures.preview.title")}
          </Typography>
        )}
        {(showInline || showHtml || showUrl) && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <IconButton
              size="small"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
              disabled={zoom <= ZOOM_MIN}
              aria-label={t("signatures.preview.zoomOut", "Zoom out") as string}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            {/* Click the % to snap back to 1×. Min-width keeps the bar from
                jumping when the digit count changes (75% → 100%). */}
            <Typography
              variant="caption"
              onClick={() => setZoom(1)}
              sx={{
                minWidth: 40,
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none",
                color: zoom === 1 ? "text.secondary" : "primary.main"
              }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton
              size="small"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
              disabled={zoom >= ZOOM_MAX}
              aria-label={t("signatures.preview.zoomIn", "Zoom in") as string}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
        {showUrl && url && (
          <IconButton component="a" href={url} target="_blank" rel="noreferrer">
            <OpenInNewIcon />
          </IconButton>
        )}
        <IconButton onClick={onClose} aria-label={t("close") as string}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {showInline && template && ctx && (
          <IframeTemplatePreview template={template} ctx={ctx} zoom={zoom} />
        )}
        {showHtml && <SignatureDocumentPreview html={effectiveHtml || ""} zoom={zoom} prefilledValues={prefilledValues} />}
        {showUrl && <SignatureDocumentPreview url={url || ""} zoom={zoom} />}
        {!showInline && !showHtml && !showUrl && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Typography color="text.secondary">{t("signatures.preview.emptyHelper")}</Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
